import { useCallback, useRef, useState } from "react";

// Which command dialog is open, with an id for each time one opens (CHT-750 review F2, F3). The id has two jobs:
//   - A completion or close callback carries the id of the dialog that made it, and is ignored when that dialog is no longer the open
//     one. An obsolete `onDone` can never close a newer dialog.
//   - The id is the scope of the idempotency keys (`commands.ts`): a key lives only for the retries inside one dialog, so a new dialog
//     with identical text sends a new key.

type OpenDialog<T> = Readonly<{ id: string; action: T; target: string | null }>;

export function useDialogSession<T>() {
  const [current, setCurrent] = useState<OpenDialog<T> | null>(null);
  const currentId = useRef<string | null>(null);
  const counter = useRef(0);

  const open = useCallback((action: T, target: string | null = null): string => {
    counter.current += 1;
    const id = `dialog-${counter.current}`;
    currentId.current = id;
    setCurrent({ id, action, target });
    return id;
  }, []);

  const close = useCallback(() => {
    currentId.current = null;
    setCurrent(null);
  }, []);

  /** True while `id` is the dialog that is open. Reads a ref, so a callback from an older render sees the truth. */
  const isCurrent = useCallback((id: string) => currentId.current === id, []);

  return { current, open, close, isCurrent };
}
